import React, { useState, useEffect } from "react";
import "./Report.css";
import Form from "./Form";
import CB from "./CB";
import Cables from "./Cables"; 
import RelaysOne from "./RelaysOne";
import RelaysCTOne from "./RelaysCTOne"; 
import TransformerOne from "./TransformerOne";
import VTHome from "./VTHome";
import BusBar from "./BusBar";
import { API_URL } from "./API";

const CreateReport = ({ goBack, project }) => {
  const [selectedBlock, setSelectedBlock] = useState("");
  const [blockNumber, setBlockNumber] = useState(0);
  const [showBlockNumberInput, setShowBlockNumberInput] = useState(false);
  const [showAddBlockInput, setShowAddBlockInput] = useState(false);
  const [newBlockNumber, setNewBlockNumber] = useState("");
  
  const getInitialBlockList = () => {
    try {
      const savedBlockList = localStorage.getItem('persistentBlockList');
      if (savedBlockList) {
        const parsed = JSON.parse(savedBlockList);
        const withoutAdd = parsed.filter(item => item !== "Add");
        const withoutDuplicateBlocks = withoutAdd.filter((item, index, self) => 
          item !== "Block" || self.indexOf("Block") === index
        );
        return [...withoutDuplicateBlocks, "Add"];
      }
    } catch (error) {
      
    }
    return ["Switching Yard", "Block", "MCP", "Add"];
  };

  const [customBlocks, setCustomBlocks] = useState(() => {
    try {
      const saved = localStorage.getItem('customBlocks');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      
      return [];
    }
  });

  const [blockList, setBlockList] = useState(getInitialBlockList());
  const [currentCard, setCurrentCard] = useState("main");
  const [plantName, setPlantName] = useState("");
  const [blockNumberError, setBlockNumberError] = useState("");
  const [newSubProjectName, setNewSubProjectName] = useState("");
  const [equipmentList, setEquipmentList] = useState([""]);
  const [equipmentTypeList, setEquipmentTypeList] = useState([""]);
  const [currentEquipmentIndex, setCurrentEquipmentIndex] = useState(0);
  const [selectedInstrument, setSelectedInstrument] = useState("");
  const [instrumentList] = useState(["CT", "CB", "Relays","RelaysCT", "VT"]);
  const [equipmentOptions] = useState(["HT Panel", "LT Panel", "Transformers", "Cables"]);
  const [panelTypeOptions] = useState(["Incoming", "Outgoing", "Bus BT", "Bus Coupler", "Bus Bar"]);
  const [showCTForm, setShowCTForm] = useState(false);
  const [showCBForm, setShowCBForm] = useState(false);
  const [showCableForm, setShowCableForm] = useState(false);  
  const [showRelayForm, setShowRelayForm] = useState(false); 
  const [showRelayCTForm, setShowRelayCTForm] = useState(false);
  const [showVTForm, setShowVTForm] = useState(false);
  const [showTransformerForm, setShowTransformerForm] = useState(false);
  const [showBusBarForm, setShowBusBarForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [addSubProjectError, setAddSubProjectError] = useState("");

  const [reportData, setReportData] = useState({
    project_id: project?.project_id || project?.id || null,
    project_name: project?.name || project?.project_name || null,
    client_name: project?.clientName || project?.client_name || null, 
    vendor_name: project?.vendorName || project?.vendor_name || null, 
    location: project?.location || null,
    customer: project?.customer || null,
    numberOfBlocks: project?.numberOfBlocks || project?.no_of_blocks || null,
    sub_project: "",
    plant: "",
    equipment: [],
    instrument: "",
    status: project?.status || null,
    creation_date: new Date().toISOString(),
    last_modified: new Date().toISOString()
  });

  const saveBlockListToStorage = (newBlockList) => {
    try {
      localStorage.setItem('persistentBlockList', JSON.stringify(newBlockList));
    } catch (error) {
      
    }
  };

  React.useEffect(() => {
    const finalBlockName = selectedBlock === "Block" ? `BLOCK-${blockNumber}` : selectedBlock;
    
    setReportData(prevData => ({
      ...prevData,
      project_id: project?.project_id || project?.id || prevData.project_id,
      sub_project: finalBlockName,
      plant: plantName,
      equipment: equipmentList.filter(eq => eq !== ""),
      equipment_types: equipmentTypeList.filter((_, i) => equipmentList[i] !== ""),
      instrument: selectedInstrument
    }));
  }, [selectedBlock, blockNumber, plantName, equipmentList, equipmentTypeList, selectedInstrument, project]);

  const handleBlockSelection = (value) => {
    setSelectedBlock(value);
    
    if (value === "Block") {
      setShowBlockNumberInput(true);
      setBlockNumber(0);
    } else {
      setShowBlockNumberInput(false);
      setBlockNumber(0);
    }
  };

  const handleBlockNumberChange = (value) => {
    if (typeof value === 'string') {
      const numMatch = value.match(/\d+/);
      const numValue = numMatch ? parseInt(numMatch[0]) : 0;
      setBlockNumber(numValue);
      if (numValue > 0) {
        setBlockNumberError("");
      }
    } else if (value >= 0) {
      setBlockNumber(value);
      if (value > 0) {
        setBlockNumberError("");
      }
    }
  };

  const incrementBlockNumber = () => {
    setBlockNumber(prev => {
      const newValue = prev + 1;
      if (newValue > 0) {
        setBlockNumberError("");
      }
      return newValue;
    });
  };

  const decrementBlockNumber = () => {
    setBlockNumber(prev => {
      const newValue = Math.max(0, prev - 1);
      return newValue;
    });
  };

  const handleBlockNext = () => {
    if (selectedBlock === "Block" && blockNumber === 0) {
      setBlockNumberError("Block number cannot be zero! Please select a valid block number.");
      return; 
    }
    
    setBlockNumberError("");
    
    if (selectedBlock === "Add") {
      setCurrentCard("addSubProject");
    } else {
      setCurrentCard("equipment");
    }
  };

  const handleAddSubProject = () => {
    if (newSubProjectName.trim() !== "") {
      let trimmedName = newSubProjectName.trim();
      
      const formatBlockName = (name) => {
        if (name.toLowerCase().startsWith("block-")) {
          const parts = name.split("-");
          const numberPart = parts.slice(1).join("-");
          return "Block-" + numberPart;
        }
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
      };
      
      trimmedName = formatBlockName(trimmedName);
      
      const normalizedInput = trimmedName.toUpperCase();
      const isDuplicate = blockList.some(block => 
        block.toUpperCase() === normalizedInput
      );
      
      if (isDuplicate) {
        setAddSubProjectError(`${trimmedName} already exists!`);
        return;
      }
      
      if (trimmedName.toUpperCase().startsWith("BLOCK-")) {
        const blockNum = trimmedName.substring(6);
        
        const numValue = parseInt(blockNum);
        const cleanBlockNum = isNaN(numValue) ? blockNum : numValue.toString();
        
        const isBlockDuplicate = customBlocks.some(block => 
          parseInt(block) === parseInt(cleanBlockNum)
        );
        
        if (isBlockDuplicate) {
          setAddSubProjectError(`Block-${cleanBlockNum} already exists!`);
          return;
        }
        
        if (!customBlocks.includes(cleanBlockNum)) {
          const updatedCustomBlocks = [...customBlocks, cleanBlockNum].sort((a, b) => {
            const numA = parseInt(a) || 0;
            const numB = parseInt(b) || 0;
            return numA - numB;
          });
          setCustomBlocks(updatedCustomBlocks);
          localStorage.setItem('customBlocks', JSON.stringify(updatedCustomBlocks));
        }
        setSelectedBlock("Block");
        setShowBlockNumberInput(true);
        setBlockNumber(parseInt(cleanBlockNum) || 0);
      } else {
        if (!blockList.includes(trimmedName)) {
          const newList = [...blockList];
          const addIndex = newList.indexOf("Add");
          newList.splice(addIndex, 0, trimmedName);
          setBlockList(newList);
          saveBlockListToStorage(newList);
        }
        setSelectedBlock(trimmedName);
      }
      
      setNewSubProjectName("");
      setAddSubProjectError("");
      setCurrentCard("main");
    }
  };

  const handlePlantNext = () => {
    if (plantName.trim() !== "" && !blockList.includes(plantName) && plantName !== "Block") {
      const newList = [...blockList];
      const addIndex = newList.indexOf("Add");
      newList.splice(addIndex, 0, plantName);
      
      setBlockList(newList);
      saveBlockListToStorage(newList);
      setSelectedBlock(plantName); 
    }
    
    setCurrentCard("main");
  };

  const resetBlockList = () => {
    const defaultList = ["Switching Yard", "Block", "MCP", "Add"];
    setBlockList(defaultList);
    saveBlockListToStorage(defaultList);
  };

  const removeFromBlockList = (itemToRemove) => {
    if (itemToRemove === "Add" || itemToRemove === "Block") return; 
    
    const newList = blockList.filter(item => item !== itemToRemove);
    setBlockList(newList);
    saveBlockListToStorage(newList);
    
    if (selectedBlock === itemToRemove) {
      setSelectedBlock("");
    }
  };

  const handleEquipmentNext = () => {
    for (let i = 0; i < equipmentList.length; i++) {
      const equipment = equipmentList[i];
      const equipmentType = equipmentTypeList[i];
      
      if ((equipment === "HT Panel" || equipment === "LT Panel") && !equipmentType) {
        setValidationError(`Please select a panel type for ${equipment}`);
        return; 
      }
    }
    
    setValidationError("");
    
    const hasCables = equipmentList.some(eq => eq === "Cables");
    const hasTransformers = equipmentList.some(eq => eq === "Transformers");
    const hasBusBar = equipmentTypeList.some(type => type === "Bus Bar");
    
    if (hasCables || hasTransformers || hasBusBar) {
      setCurrentCard("finalPreview");
    } else {
      setCurrentCard("instrument");
    }
  };

  const handleInstrumentNext = () => {
    setCurrentCard("finalPreview");
  };

  const handleEquipmentChange = (index, value) => {
    const newEquipmentList = [...equipmentList];
    newEquipmentList[index] = value;
    setEquipmentList(newEquipmentList);
    
    const newEquipmentTypeList = [...equipmentTypeList];
    newEquipmentTypeList[index] = "";
    setEquipmentTypeList(newEquipmentTypeList);
    
    setValidationError("");
  };

  const handleEquipmentTypeChange = (index, value) => {
    const newEquipmentTypeList = [...equipmentTypeList];
    newEquipmentTypeList[index] = value;
    setEquipmentTypeList(newEquipmentTypeList);
    
    setValidationError("");
  };

  const handleAddMoreEquipment = () => {
    for (let i = 0; i < equipmentList.length; i++) {
      const equipment = equipmentList[i];
      const equipmentType = equipmentTypeList[i];
      
      if ((equipment === "HT Panel" || equipment === "LT Panel") && !equipmentType) {
        setValidationError(`Please select a panel type for ${equipment} before adding more equipment`);
        return; 
      }
    }
    
    setValidationError("");
    
    setEquipmentList([...equipmentList, ""]);
    setEquipmentTypeList([...equipmentTypeList, ""]);
    setCurrentEquipmentIndex(currentEquipmentIndex + 1);
  };

  const submitToBackend = async (data) => {
    try {
      setIsSubmitting(true);
     
      
      const response = await fetch(`${API_URL}/test_report/create_update_test_report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const result = await response.json();
     
      alert("Report submitted successfully!");
      
    } catch (error) {
     
      alert("Failed to submit report. Please try again.");
    } finally {
      setIsSubmitting(false);
      goBack();
    }
  };

  const getFormattedEquipmentList = () => {
    return equipmentList.map((equipment, index) => {
      if ((equipment === "HT Panel" || equipment === "LT Panel") && equipmentTypeList[index]) {
        return `${equipment} (${equipmentTypeList[index]})`;
      }
      return equipment;
    }).filter(eq => eq !== "").join(", ");
  };

  const handleSubmit = () => {
    for (let i = 0; i < equipmentList.length; i++) {
      const equipment = equipmentList[i];
      const equipmentType = equipmentTypeList[i];
      
      if ((equipment === "HT Panel" || equipment === "LT Panel") && !equipmentType) {
        setValidationError(`Please select a panel type for ${equipment}`);
        return; 
      }
    }
    
    setValidationError("");
    
    const getProjectId = () => {
      
     
      
      if (project?.project_id) {
        const id = parseInt(project.project_id);
        if (!isNaN(id) && id > 0) {
         
          return id;
        }
      }
      
      if (project?.id) {
        const id = parseInt(project.id);
        if (!isNaN(id) && id > 0) {
       
          return id;
        }
      }
      
      return 1;
    };
    
    const projectId = getProjectId();
    

    const finalBlockName = selectedBlock === "Block" ? `BLOCK-${blockNumber}` : selectedBlock;

    const testData = {
      "description": `${selectedInstrument || equipmentList[0]} Report for ${finalBlockName}`,
      "block_no": finalBlockName,
      "date": new Date().toISOString().split('T')[0],
      "customer": project?.customer || "Customer Name",
      "project": project?.name || project?.project_name || "",
      "client_name": project?.clientName || project?.client_name || "Client Name", 
      "vendor_name": project?.vendorName || project?.vendor_name || "Vendor Name",
      "location": project?.location || "",
      "numberOfBlocks": project?.numberOfBlocks || project?.no_of_blocks || "1",
      "report_no": `${projectId}-${Date.now()}`,
      "yom": new Date().getFullYear(),
    };
    
    const payload = {
      "equipment_name": selectedInstrument || equipmentList[0],
      "category_name": equipmentList[currentEquipmentIndex] || "",
      "subcategory_name": equipmentTypeList[currentEquipmentIndex] || "General",
      "sub_project_name": finalBlockName,
      "project_id": projectId,
      "test_date": new Date().toISOString().split('T')[0],
      "test_data": testData,
      "project_name": project?.name || project?.project_name || "",
      "location": project?.location || ""
    };
    

    
    localStorage.setItem('createReportData', JSON.stringify(payload));
    
    const storedData = localStorage.getItem('createReportData');
    if (storedData) {
      const parsed = JSON.parse(storedData);
     
    }
    
    const hasCables = equipmentList.some(eq => eq === "Cables");
    const hasTransformers = equipmentList.some(eq => eq === "Transformers");
    const hasBusBar = equipmentTypeList.some(type => type === "Bus Bar");
    
    if (hasBusBar) {
      setShowBusBarForm(true);
    } else if (selectedInstrument === "Relays") {
      setShowRelayForm(true);
    } else if (hasTransformers) {
      setShowTransformerForm(true);
    } else if (hasCables) {
      setShowCableForm(true);
    } else if (selectedInstrument === "CT") {
      setShowCTForm(true);
    } else if (selectedInstrument === "CB") {
      setShowCBForm(true);
    } else if (selectedInstrument === "RelaysCT") {
      setShowRelayCTForm(true);
    } else if (selectedInstrument === "VT") {
      setShowVTForm(true);
    } else {
      submitToBackend(payload);
    }
  };

  const handleBackFromForm = () => {
    setShowCTForm(false);
    setShowCBForm(false);
    setShowCableForm(false);
    setShowRelayForm(false); 
    setShowRelayCTForm(false);
    setShowVTForm(false);
    setShowTransformerForm(false);
    setShowBusBarForm(false);
    goBack();
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const getNextButtonText = () => {
    if (selectedBlock === "Select" || selectedBlock === "Add") {
      return "Add";
    }
    return "Next";
  };

  const isPanelType = (equipmentType) => {
    return equipmentType === "HT Panel" || equipmentType === "LT Panel";
  };

  const getDisplayBlockName = () => {
    if (selectedBlock === "Block") {
      return `Block-${blockNumber}`;
    }
    return selectedBlock;
  };

  const renderPreviewContent = () => {
    return (
      <div className="preview-content">
        {project && <p><strong>Project:</strong> {project.name}</p>}
        {selectedBlock && selectedBlock !== "Select" && <p><strong>Block:</strong> {getDisplayBlockName()}</p>}
        {plantName && <p><strong>Plant:</strong> {plantName}</p>}
        {equipmentList.some(eq => eq !== "") && (
          <p><strong>Equipment:</strong> {getFormattedEquipmentList()}</p>
        )}
        {selectedInstrument && <p><strong>Instrument:</strong> {selectedInstrument}</p>}
      </div>
    );
  };

  const renderCurrentCard = () => {
    if (showBusBarForm) {
      return (
        <div className="form-container">
          <BusBar
            block={getDisplayBlockName()} 
            plant={plantName}
            project={{
              ...project,
              project_id: project?.project_id || project?.id,
              id: project?.project_id || project?.id
            }}
            equipmentType={equipmentTypeList.find(type => type === "Bus Bar")}
            goBack={handleBackFromForm} 
            data={JSON.parse(localStorage.getItem('createReportData'))}
          />
        </div>
      );
    }
    
    if (showTransformerForm) {
      return (
        <div className="form-container">
          <TransformerOne
            block={getDisplayBlockName()} 
            plant={plantName}
            project={{
              ...project,
              project_id: project?.project_id || project?.id,
              id: project?.project_id || project?.id
            }}
            goBack={handleBackFromForm} 
          />
        </div>
      );
    }
    
    if (showCableForm) {
      return (
        <div className="form-container">
          <Cables 
            block={getDisplayBlockName()} 
            plant={plantName} 
            project={{
              ...project,
              project_id: project?.project_id || project?.id,
              id: project?.project_id || project?.id
            }}
            goBack={handleBackFromForm} 
          />
        </div>
      );
    }
    
    if (showCTForm) {
      return (
        <div className="form-container">
          <Form 
            block={getDisplayBlockName()} 
            plant={plantName} 
            instrument={selectedInstrument} 
            project={{
              ...project,
              project_id: project?.project_id || project?.id,
              id: project?.project_id || project?.id
            }}
            goBack={handleBackFromForm} 
          />
        </div>
      );
    }

    if (showCBForm) {
      return (
        <div className="form-container">
          <CB
            block={getDisplayBlockName()} 
            plant={plantName} 
            instrument={selectedInstrument} 
            project={{
              ...project,
              project_id: project?.project_id || project?.id,
              id: project?.project_id || project?.id
            }}
            goBack={handleBackFromForm} 
          />
        </div>
      );
    }

    if (showRelayForm) {
      return (
        <div className="form-container">
          <RelaysOne
            block={getDisplayBlockName()} 
            plant={plantName} 
            instrument={selectedInstrument} 
            project={{
              ...project,
              project_id: project?.project_id || project?.id,
              id: project?.project_id || project?.id
            }}
            goBack={handleBackFromForm} 
          />
        </div>
      );
    }

    if (showRelayCTForm) {
      return (
        <div className="form-container">
          <RelaysCTOne
            block={getDisplayBlockName()} 
            plant={plantName} 
            instrument={selectedInstrument} 
            project={{
              ...project,
              project_id: project?.project_id || project?.id,
              id: project?.project_id || project?.id
            }}
            goBack={handleBackFromForm} 
          />
        </div>
      );
    }

    if (showVTForm) {
      return (
        <div className="form-container">
          <VTHome
            block={getDisplayBlockName()} 
            plant={plantName} 
            instrument={selectedInstrument} 
            project={{
              ...project,
              project_id: project?.project_id || project?.id,
              id: project?.project_id || project?.id
            }}
            goBack={handleBackFromForm} 
          />
        </div>
      );
    }

    switch (currentCard) {
      case "addSubProject":
        return (
          <div className="popup-card">
            <h2>ADD SUB PROJECT</h2>
            <div className="card-content">
              <input 
                type="text" 
                value={newSubProjectName} 
                onChange={(e) => {
                  setNewSubProjectName(e.target.value);
                  setAddSubProjectError("");
                }}
                placeholder="Enter sub project name (e.g., Block-1)" 
              />
              
              {addSubProjectError && (
                <div style={{
                  color: 'red',
                  fontSize: '14px',
                  marginTop: '10px',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  {addSubProjectError}
                </div>
              )}
              
              {showPreview && (
                <div className="current-preview">
                  <h3>Current Selection</h3>
                  {renderPreviewContent()}
                </div>
              )}
            </div>
            <div className="button-row9999">
              <button className="preview-button" onClick={togglePreview}>
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
            </div>
            <div className="button-row9999">
              <button className="control-button" onClick={() => {
                setCurrentCard("main");
                setNewSubProjectName("");
                setAddSubProjectError("");
              }}>Back</button>
              <button 
                className="control-button" 
                onClick={handleAddSubProject}
                disabled={!newSubProjectName.trim()}
              >
                Add
              </button>
            </div>
          </div>
        );

      case "plant":
        return (
          <div className="popup-card">
            <h2>ADD PLANT</h2>
            <div className="card-content">
              <input type="text" value={plantName} onChange={(e) => setPlantName(e.target.value)} placeholder="Enter plant name" />
              
              {showPreview && (
                <div className="current-preview">
                  <h3>Current Selection</h3>
                  {renderPreviewContent()}
                </div>
              )}
            </div>
            <div className="button-row9999">
              <button className="preview-button" onClick={togglePreview}>
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
            </div>
            <div className="button-row9999">
              <button className="control-button" onClick={() => setCurrentCard("main")}>Back</button>
              <button 
                className="control-button" 
                onClick={handlePlantNext}
              >
                Add
              </button>
            </div>
          </div>
        );
      
      case "equipment":
        return (
          <div className="popup-card">
            <h2>ADD CATEGORY</h2>
            <div className="card-content">
              <div className="equipment-selection">
                <select 
                  value={equipmentList[currentEquipmentIndex]} 
                  onChange={(e) => handleEquipmentChange(currentEquipmentIndex, e.target.value)}
                  className="equipment-select"
                >
                  <option value="">Select Category</option>
                  {equipmentOptions.map((equipment, index) => (
                    <option key={index} value={equipment}>{equipment}</option>
                  ))}
                </select>
                
                {isPanelType(equipmentList[currentEquipmentIndex]) && (
                  <select 
                    value={equipmentTypeList[currentEquipmentIndex]} 
                    onChange={(e) => handleEquipmentTypeChange(currentEquipmentIndex, e.target.value)}
                    className="panel-type-select"
                  >
                    <option value="dep">Select SubCategory</option>
                    {panelTypeOptions.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                )}
              </div>
              
              {validationError && (
                <div className="validation-error" style={{ color: 'red', marginTop: '10px', fontWeight: 'bold' }}>
                  {validationError}
                </div>
              )}
              
              {showPreview && (
                <div className="current-preview">
                  <h3>Current Selection</h3>
                  {renderPreviewContent()}
                </div>
              )}
            </div>
            <div className="button-row9999">
              <button 
                className="action-button108" 
                onClick={handleAddMoreEquipment}
              >
                Add More
              </button>
              <button className="preview-button" onClick={togglePreview}>
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
            </div>
            <div className="button-row9999">
              <button className="control-button" onClick={() => setCurrentCard("main")}>Back</button>
              <button 
                className="control-button" 
                onClick={handleEquipmentNext}
              >
                Next
              </button>
            </div>
          </div>
        );
      
      case "instrument":
        return (
          <div className="popup-card">
            <h2>ADD EQUIPMENT</h2>
            <div className="card-content">
              <select value={selectedInstrument} onChange={(e) => setSelectedInstrument(e.target.value)}>
                <option value="">Select Equipment</option>
                {instrumentList.map((instrument, index) => (
                  <option key={index} value={instrument}>{instrument}</option>
                ))}
              </select>
              
              {showPreview && (
                <div className="current-preview">
                  <h3>Current Selection</h3>
                  {renderPreviewContent()}
                </div>
              )}
            </div>
            <div className="button-row9999">
              <button className="preview-button" onClick={togglePreview}>
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
            </div>
            <div className="button-row9999">
              <button className="control-button" onClick={() => setCurrentCard("equipment")}>Back</button>
              <button 
                className="control-button" 
                onClick={handleInstrumentNext}
              >
                Next
              </button>
            </div>
          </div>
        );
      
case "finalPreview":
  const hasTransformersOrCables = equipmentList.some(eq => 
    eq === "Transformers" || eq === "Cables"
  );
  const hasBusBar = equipmentTypeList.some(type => type === "Bus Bar");
  
  return (
    <div className="popup-card">
      <h2>FINAL PREVIEW</h2>
      <div className="card-content preview-content">
        {project && <p><strong>Project:</strong> {project.name}</p>}
        <p><strong>Sub Project:</strong> {getDisplayBlockName()}</p>
        {plantName && <p><strong>Plant:</strong> {plantName}</p>}
        <p><strong>Equipment:</strong> {getFormattedEquipmentList()}</p>
        {/* Only show instrument if it's not a Bus Bar, Transformer, or Cable */}
        {!hasTransformersOrCables && !hasBusBar && selectedInstrument && (
          <p><strong>Instrument:</strong> {selectedInstrument}</p>
        )}
        {/* Show Bus Bar as a separate line if selected */}
        {hasBusBar && (
          <p><strong>SubCategory:</strong> Bus Bar</p>
        )}
      </div>
      <div className="button-row9999">
        <button className="control-button" onClick={() => {
          if (hasTransformersOrCables || hasBusBar) {
            setCurrentCard("equipment");
          } else {
            setCurrentCard("instrument");
          }
        }}>Back</button>
        <button 
          className="control-button" 
          onClick={handleSubmit} 
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Confirm"}
        </button>
      </div>
    </div>
  );
      
      default: 
        return (
          <div className="popup-card">
            <h2>CREATE REPORT</h2>
            <div className="card-content">
              <div className="form-group">
                <div className="block-label">Select SubProject:</div>
                <select 
                  value={selectedBlock} 
                  onChange={(e) => handleBlockSelection(e.target.value)}
                  className="block-select"
                >
                  <option value="">Select</option>
                  {blockList.map((block, index) => (
                    <option key={index} value={block}>{block}</option>
                  ))}
                </select>
              </div>
              
              {showBlockNumberInput && (
                <div className="form-group" style={{ marginTop: '20px', width: '500px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label className="block-label">Block Number:</label>
                    <button
                      onClick={() => setShowAddBlockInput(true)}
                      style={{
                        padding: '6px 20px',
                        fontSize: '14px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginTop:"-270px",
                        marginLeft:"132px",
                      }}
                    >
                      Add
                    </button>
                  </div>

                  {customBlocks.length > 0 ? (
                    <select
                      value={blockNumber}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        if (selectedValue === "0") {
                          handleBlockNumberChange(0);
                        } else {
                          const option = customBlocks.find(block => {
                            const numMatch = block.match(/\d+/);
                            return numMatch && parseInt(numMatch[0]) === parseInt(selectedValue);
                          });
                          if (option) {
                            handleBlockNumberChange(option);
                          }
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '16px',
                        border: blockNumberError ? '2px solid red' : '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="0">Select Block Number</option>
                      {customBlocks.map((blockNum) => {
                        const numMatch = blockNum.match(/\d+/);
                        const numValue = numMatch ? numMatch[0] : blockNum;
                        const displayText = `Block-${numValue}`;
                        return (
                          <option key={blockNum} value={numValue}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#666', border: 'none ',marginLeft: '-160px' }}>
                      No blocks added yet. Click "Add" to create one.
                    </div>
                  )}

                  {blockNumberError && (
                    <div style={{
                      color: 'red',
                      fontSize: '14px',
                      marginTop: '8px',
                      fontWeight: 'bold'
                    }}>
                      {blockNumberError}
                    </div>
                  )}

                  {showAddBlockInput && (
                    <div style={{
                      position: 'fixed',
                      top: '0',
                      left: '0',
                      right: '0',
                      bottom: '0',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      zIndex: 1000
                    }}>
                      <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        minWidth: '400px'
                      }}>
                        <h3 style={{ marginTop: '0', marginBottom: '20px', textAlign: 'center' }}>Add Block Number</h3>
                        <div style={{ position: 'relative', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                          <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
                            <span style={{
                              position: 'absolute',
                              left: '50%',
                              top: '30%',
                              transform: 'translate(-100%, -50%)',
                              fontSize: '16px',
                              color: '#333',
                              pointerEvents: 'none',
                              marginLeft: '-5px'
                            }}>
                              Block-
                            </span>
                            <input
                              type="text"
                              value={newBlockNumber}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, '');
                                setNewBlockNumber(value);
                                setBlockNumberError("");
                              }}
                              placeholder="Enter number"
                              style={{
                                width: '100%',
                                padding: '10px',
                                paddingLeft: '50%',
                                fontSize: '16px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                boxSizing: 'border-box',
                                textAlign: 'left'
                              }}
                            />
                          </div>
                        </div>
                        {blockNumberError && (
                          <div style={{
                            color: 'red',
                            fontSize: '14px',
                            marginBottom: '10px',
                            fontWeight: 'bold',
                            textAlign: 'center'
                          }}>
                            {blockNumberError}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                          <button
                            onClick={() => {
                              setNewBlockNumber("");
                              setShowAddBlockInput(false);
                              setBlockNumberError("");
                            }}
                            style={{
                              padding: '8px 20px',
                              fontSize: '16px',
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              if (newBlockNumber.trim()) {
                                const numValue = parseInt(newBlockNumber.trim());
                                const cleanBlockNum = numValue.toString();
                                const normalizedNewBlock = `Block-${cleanBlockNum}`;
                                
                                const isDuplicate = customBlocks.some(existing => {
                                  return parseInt(existing) === numValue;
                                });
                                
                                if (isDuplicate) {
                                  setBlockNumberError(`${normalizedNewBlock} already exists!`);
                                  return;
                                }
                                
                                const updatedCustomBlocks = [...customBlocks, cleanBlockNum].sort((a, b) => {
                                  return parseInt(a) - parseInt(b);
                                });
                                setCustomBlocks(updatedCustomBlocks);
                                localStorage.setItem('customBlocks', JSON.stringify(updatedCustomBlocks));
                                
                                setBlockNumber(numValue);
                                setBlockNumberError("");
                                setNewBlockNumber("");
                                setShowAddBlockInput(false);
                              }
                            }}
                            style={{
                              padding: '8px 20px',
                              fontSize: '16px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showPreview && (
                <div className="current-preview">
                  <h3>Current Selection</h3>
                  {renderPreviewContent()}
                </div>
              )}
            </div>
            
            <div className="button-row9999">
              <button className="preview-button" onClick={togglePreview}>
                {showPreview ? "Hide Preview" : "Show Preview"}
              </button>
            </div>
            
            <div className="bottom-buttons">
              <button className="control-button" onClick={goBack}>Cancel</button>
              <button 
                className="control-button" 
                onClick={handleBlockNext}
                disabled={
                  !selectedBlock || 
                  selectedBlock === "Select" || 
                  (selectedBlock === "Block" && blockNumber === 0) 
                }
              >
                {getNextButtonText()}
              </button>
            </div>
          </div>
        );
    }
  };

  return <div className="popup-overlay">{renderCurrentCard()}</div>;
};

export default CreateReport;